import { DateAdapter, MatDateFormats } from '@angular/material/core';
import { isMoment, Moment } from 'moment';
import * as moment from 'moment';
import { mask } from 'remask';

export const DATE_MASK = '99/99/9999';

export const MOMENT_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM Y',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM Y',
  },
};

const dateNames: string[] = [];

for (let date = 1; date <= 31; date++) {
  dateNames.push(String(date));
}

export class MomentDateAdapter extends DateAdapter<Moment> {
  invalid(): Moment {
    return moment.invalid();
  }

  private localeData = moment.localeData('pt-br');

  getYear(date: Moment | string): number {
    return this.handleDate(date).year();
  }

  getMonth(date: Moment | string): number {
    return this.handleDate(date).month();
  }

  getDate(date: Moment | string): number {
    return this.handleDate(date).date();
  }

  getDayOfWeek(date: Moment | string): number {
    return this.handleDate(date).day();
  }

  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return this.localeData.months();
      case 'short':
        return this.localeData.monthsShort();
      case 'narrow':
        return this.localeData.monthsShort().map((month) => month[0]);
    }
  }

  getDateNames(): string[] {
    return dateNames;
  }

  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    switch (style) {
      case 'long':
        return this.localeData.weekdays();
      case 'short':
        return this.localeData.weekdaysShort();
      case 'narrow':
        return this.localeData.weekdaysShort();
    }
  }

  getYearName(date: Moment | string): string {
    return String(this.handleDate(date).year());
  }

  getFirstDayOfWeek(): number {
    return this.localeData.firstDayOfWeek();
  }

  getNumDaysInMonth(date: Moment | string): number {
    return this.handleDate(date).daysInMonth();
  }

  clone(date: Moment | string): Moment {
    return this.handleDate(date).clone();
  }

  createDate(year: number, month: number, date: number): Moment {
    return moment([year, month, date]);
  }

  today(): Moment {
    return moment();
  }

  parse(value: string): Moment | any {
    const masked = mask(value, DATE_MASK);
    const m = moment(masked, MOMENT_DATE_FORMATS.display.dateInput, true);

    if (!m.isValid()) {
      return null;
    }

    return m;
  }

  /**
   * Converts a Moment date to a string in the given format
   * and add 3 hours to fix timezone
   */
  format(value: Moment | string, displayFormat: any): string {
    const date = isMoment(value) ? value : moment(value, MOMENT_DATE_FORMATS.parse.dateInput, true);

    if (date) {
      if (typeof value == 'string')
        date.add(3, 'hours');
      return date.format(displayFormat);
    }
    
    return '';
  }

  addCalendarYears(date: Moment, years: number): Moment {
    return date.clone().add(years, 'y');
  }

  addCalendarMonths(date: Moment, months: number): Moment {
    return date.clone().add(months, 'M');
  }

  addCalendarDays(date: Moment, days: number): Moment {
    return date.clone().add(days, 'd');
  }

  override setLocale(locale: any): void {
    this.localeData = moment.localeData(locale);
  }

  override compareDate(first: Moment, second: Moment): number {
    return first.diff(second, 'seconds', true);
  }

  override sameDate(first: any | Moment, second: any | Moment): boolean {
    if (first == null) {
      return second == null;
    } else if (isMoment(first)) {
      return first.isSame(second);
    }
    return super.sameDate(first, second);
  }

  override clampDate(date: Moment | string, min?: any | Moment, max?: any | Moment): Moment {
    date = this.handleDate(date);
    
    if (min && date.isBefore(min)) {
      return min;
    } else if (max && date.isAfter(max)) {
      return max;
    }
    return date;
  }

  isValid(value: Moment | string): boolean {
    const date = isMoment(value) ? value : moment(value, MOMENT_DATE_FORMATS.parse.dateInput, true);
    return date.isValid();
  }

  isDateInstance(value: Object | string): boolean {
    return (
      isMoment(value) || moment(value, MOMENT_DATE_FORMATS.parse.dateInput, true).isValid()
    );
  }

  toIso8601(date: Moment): string {
    return date.format();
  }

  /**
   * Tratar datas que vieram do banco de dados com 3 horas a menos
   */
  handleDate(date: Moment | string): Moment {
    if(typeof date === 'string') {
      return moment(date, MOMENT_DATE_FORMATS.parse.dateInput, true).add(3, 'hours');
    } else if (isMoment(date)) {
      return date;
    } else {
      return date;
    }
  }

  fromIso8601(iso8601String: string): Moment {
    return moment(iso8601String);
  }
}
